import { useEffect, useState } from "react";
import { MODULE_LABELS } from "@dentacore/shared-types";
import type { ModuleName } from "@dentacore/shared-types";

import styles from "./operator-page.module.css";
import { clinicBilling, saveOverride } from "./operator-api";
import type { ClinicBilling } from "./operator-api";
import { priceCents, priceText } from "./price-format";

type Item = "base" | "bundle" | ModuleName;

const LABELS: Record<string, string> = { base: "Base plan", bundle: "Everything bundle" };
const labelOf = (item: Item): string => LABELS[item] ?? MODULE_LABELS[item as ModuleName];

/**
 * **What this clinic is charged this month** (STORY-190a, BL-D2, BL-D5, BL-D8,
 * BL-D12, BL-D16): the base band by its active branches, each add-on it is billed
 * for, the bundle when it has all three, and the clinic's own prices where it has
 * them. **A line with no price shows as such and the total is blank** rather than
 * a figure that leaves something out. DentaCore charges nothing — this is what
 * you invoice from (BL-D1).
 */
export function BillingPanel({
  clinicId,
  modulesKey,
}: {
  clinicId: string;
  /** Changes when a module is switched, so the bill re-reads. */
  modulesKey: string;
}): React.JSX.Element {
  const [billing, setBilling] = useState<ClinicBilling | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<Item>("base");
  const [text, setText] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    void clinicBilling(clinicId)
      .then(setBilling)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load the bill");
      });
  }, [clinicId, modulesKey]);

  const save = (cents: number | null): void => {
    setError(null);
    void saveOverride(clinicId, item, cents, note)
      .then((next) => {
        setBilling(next);
        setText("");
        setNote("");
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not save");
      });
  };
  const cents = priceCents(text);

  return (
    <section className={styles.panel} data-testid="billing-panel">
      <span className={styles.panelTitle}>Monthly bill</span>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {billing === null ? (
        <span className={styles.muted}>Loading…</span>
      ) : (
        <>
          <span className={styles.muted}>
            {billing.branchCount} active {billing.branchCount === 1 ? "branch" : "branches"}. You
            invoice outside DentaCore.
          </span>
          <ul className={styles.rows}>
            {billing.bill.lines.map((line) => (
              <li key={line.key} className={styles.row} data-testid={`bill-${line.key}`}>
                <div className={styles.rowMain}>
                  <span className={styles.rowName}>{line.label}</span>
                  <span className={styles.rowSub}>
                    {line.source === "OVERRIDE"
                      ? `This clinic's own price${line.note === null ? "" : `: ${line.note}`}`
                      : line.source === "LIST"
                        ? "From the price list"
                        : "No price set"}
                  </span>
                </div>
                <span>{line.cents === null ? "—" : priceText(line.cents)}</span>
              </li>
            ))}
          </ul>
          <p data-testid="bill-total">
            <strong>
              Total{" "}
              {billing.bill.totalCents === null
                ? "— not every line has a price"
                : `${priceText(billing.bill.totalCents)} a month`}
            </strong>
          </p>

          <span className={styles.panelTitle}>Set this clinic&rsquo;s own price</span>
          <div className={styles.row}>
            <select
              value={item}
              aria-label="Which price"
              data-testid="override-item"
              onChange={(event) => {
                setItem(event.target.value as Item);
              }}
            >
              {(["base", "bundle", "SUPPLY", "ACCOUNTING", "CHATBOT"] as Item[]).map((i) => (
                <option key={i} value={i}>
                  {labelOf(i)}
                </option>
              ))}
            </select>
            <input
              value={text}
              inputMode="decimal"
              placeholder="0.00"
              aria-label="This clinic's price per month"
              data-testid="override-price"
              onChange={(event) => {
                setText(event.target.value);
              }}
            />
            <input
              value={note}
              placeholder="Why"
              aria-label="Why this clinic pays this"
              data-testid="override-note"
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
            <button
              type="button"
              className={styles.ghost}
              disabled={cents === null}
              data-testid="override-save"
              onClick={() => {
                save(cents);
              }}
            >
              Set
            </button>
            <button
              type="button"
              className={styles.ghost}
              data-testid="override-end"
              onClick={() => {
                save(null);
              }}
            >
              Back to the list
            </button>
          </div>
          <span className={styles.muted}>
            Zero is a price: it makes the item free for this clinic. A change takes effect on the
            first of next month, or this month for an item with no earlier price.
          </span>
        </>
      )}
    </section>
  );
}
